import type { ComponentSchema, FieldSchema } from './schema-types'

type Variant = ComponentSchema['variants'][number]

/**
 * How one theme fits an inherited section schema to its own design (see docs/SECTION_FIELD_CONTRACT_PLAN.md):
 * `hide` removes fields the theme's component never draws (a key hides that key and its `key.*` children,
 * `list[].key` hides one field inside each item of a list or group; stored values are kept), `keep` limits the offered layouts to the ids the component draws differently, `add` appends
 * layouts the component draws that the inherited schema does not offer, `variantLabels` renames layouts, and
 * `labels` renames a field so it describes what the shopper sees. tests/section-contract.test.ts (storefront) checks the result.
 */
export interface SectionFit {
  hide?: string[]
  keep?: string[]
  add?: Variant[]
  variantLabels?: Record<string, string>
  labels?: Record<string, { label?: string, help?: string }>
}

/**
 * Section type → fit. Chrome sections use 'header', 'footer', 'floating-buttons' and 'mobile-bottom-nav'; the Theme tab
 * uses 'buttons' (the Buttons tab schema), 'design' (`hide: ['radius']`…) and 'store-info' (`hide: ['deliveryEta']`…).
 */
export type ThemeFit = Record<string, SectionFit>

const hidden = (key: string, hide: string[]) => hide.some(h => key === h || key.startsWith(`${h}.`))

const fitFields = (fields: FieldSchema[] | undefined, fit: SectionFit): FieldSchema[] | undefined => {
  if (!fields) return fields
  const hide = (fit.hide ?? []).filter(h => !h.includes('[].'))
  const inner = (fit.hide ?? []).filter(h => h.includes('[].')).map(h => h.split('[].') as [string, string])
  const dropInner = (f: FieldSchema): FieldSchema => {
    const gone = inner.filter(([parent]) => parent === f.key).map(([, child]) => child)
    if (!gone.length) return f
    return {
      ...f,
      ...(f.itemSchema ? { itemSchema: f.itemSchema.filter(i => !gone.includes(i.key)) } : {}),
      ...(f.fields ? { fields: f.fields.filter(i => !gone.includes(i.key)) } : {}),
    }
  }
  const kept = fields.filter(f => !hidden(f.key, hide)).map(dropInner)
  const keys = new Set(kept.map(f => f.key))
  return kept.map((f) => {
    const relabel = fit.labels?.[f.key]
    // A field shown only when a hidden field has a value would never show again: drop that condition.
    const dependency = f.dependency && !keys.has(f.dependency.field) ? undefined : f.dependency
    return { ...f, ...(relabel ?? {}), dependency }
  })
}

export const fitSchema = (schema: ComponentSchema, fit: SectionFit | undefined): ComponentSchema => {
  if (!fit) return schema
  const offered = schema.variants.filter(v => !fit.keep || fit.keep.includes(v.id))
  const variants = [...offered, ...(fit.add ?? []).filter(a => !offered.some(v => v.id === a.id))]
    .map(v => ({ ...v, label: fit.variantLabels?.[v.id] ?? v.label, fields: fitFields(v.fields, fit) }))
  return { ...schema, variants, commonFields: fitFields(schema.commonFields, fit) ?? [] }
}

interface Schemas {
  componentSchemas: Record<string, ComponentSchema>
  headerSchema?: ComponentSchema
  footerSchema?: ComponentSchema
  floatingButtonsSchema?: ComponentSchema
  mobileNavSchema?: ComponentSchema
  buttonsSchema?: ComponentSchema
}

/** Theme-tab settings a theme does not draw, hidden in the editor: Design-tab keys and Store-tab field keys. */
export interface HiddenSettings { design: string[], storeInfo: string[] }

/** A theme's schemas with its fits applied (inherited schemas are copied, never changed in place). */
export const tailorSchemas = <T extends Schemas>(schemas: T, fits: ThemeFit): T & { hiddenSettings: HiddenSettings } => ({
  ...schemas,
  componentSchemas: Object.fromEntries(Object.entries(schemas.componentSchemas).map(([type, s]) => [type, fitSchema(s, fits[type])])),
  ...(schemas.headerSchema ? { headerSchema: fitSchema(schemas.headerSchema, fits.header) } : {}),
  ...(schemas.footerSchema ? { footerSchema: fitSchema(schemas.footerSchema, fits.footer) } : {}),
  ...(schemas.floatingButtonsSchema ? { floatingButtonsSchema: fitSchema(schemas.floatingButtonsSchema, fits['floating-buttons']) } : {}),
  ...(schemas.mobileNavSchema ? { mobileNavSchema: fitSchema(schemas.mobileNavSchema, fits['mobile-bottom-nav']) } : {}),
  ...(schemas.buttonsSchema ? { buttonsSchema: fitSchema(schemas.buttonsSchema, fits.buttons) } : {}),
  hiddenSettings: { design: fits.design?.hide ?? [], storeInfo: fits['store-info']?.hide ?? [] },
})
